import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Building2, Users, Phone, TrendingUp, DollarSign, AlertTriangle, RefreshCw, Plus, Download } from 'lucide-react';
import { formatCurrency } from '../utils';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.max(0, Math.floor((now - d) / 1000));
  if (diff < 60) return 'Akkurat nå';
  if (diff < 3600) return `${Math.floor(diff/60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff/3600)} timer siden`;
  return `${Math.floor(diff/86400)} dager siden`;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [companySearch, setCompanySearch] = useState('');

  const loadOverview = useCallback(() => {
    setLoading(true);
    setFetchError(null);
    api.get('/api/admin/overview')
      .then(({ data }) => setOverview(data))
      .catch(err => { console.error(err); setFetchError('Kunne ikke laste admin-oversikt'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const exportCompaniesCSV = useCallback(() => {
    if (!overview?.company_stats?.length) return;
    const cols = ['name', 'subscription_tier', 'phone_number', 'users_count', 'calls_this_week', 'total_calls', 'monthly_cost', 'monthly_revenue', 'margin_pct', 'last_call_at', 'active', 'payment_status'];
    const header = cols.join(',');
    const rows = overview.company_stats.map(c => cols.map(k => {
      const v = c[k];
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-bedrifter-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [overview]);

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-navy-800 rounded w-64" />
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">{[1,2,3,4,5].map(i => <div key={i} className="h-28 bg-navy-800 rounded-lg" />)}</div>
      <div className="h-80 bg-navy-800 rounded-lg" />
    </div>
  );

  if (fetchError || !overview) return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="max-w-sm text-center bg-navy-800 border border-navy-600 rounded-lg p-8">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-red/15 flex items-center justify-center">
          <AlertTriangle size={20} className="text-accent-red" />
        </div>
        <p className="text-base font-semibold text-white mb-2">Kunne ikke laste admin-oversikt</p>
        <p className="text-sm text-slate-400 mb-5">{fetchError}</p>
        <button onClick={loadOverview} className="inline-flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg">
          <RefreshCw size={14} /> Prøv igjen
        </button>
      </div>
    </div>
  );

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const filteredCompanies = companySearch
    ? overview.company_stats.filter(c => c.name?.toLowerCase().includes(companySearch.toLowerCase()))
    : overview.company_stats;

  const sorted = [...filteredCompanies].sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb||'').toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ col, children, align }) => (
    <th onClick={() => toggleSort(col)} className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500 cursor-pointer hover:text-white transition-colors select-none`}>
      {children} {sortBy === col && <span className="text-accent-blue">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div data-testid="admin-dashboard-page">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Admin Oversikt</h1>
          <p className="text-slate-500 text-sm mt-1">Alle klienter og samtaler på ett sted.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/companies?new=1')}
            data-testid="admin-dash-add-company"
            className="flex items-center gap-2 px-3 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors"
            title="Legg til ny bedrift"
          >
            <Plus size={14} /> Legg til selskap
          </button>
          <button
            onClick={exportCompaniesCSV}
            data-testid="admin-dash-export-csv"
            className="flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 text-slate-400 hover:text-white text-sm rounded-lg hover:border-accent-blue transition-colors"
            title="Eksporter bedriftsliste som CSV"
          >
            <Download size={14} /> Eksport CSV
          </button>
          <button
            onClick={loadOverview}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-navy-800 border border-navy-600 text-slate-400 hover:text-white text-sm rounded-lg hover:border-accent-blue transition-colors disabled:opacity-50"
            title="Oppdater"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Oppdater
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-accent-blue/20 rounded-lg flex items-center justify-center"><Building2 size={16} className="text-accent-blue" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Bedrifter</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{overview.total_companies}</div>
          <div className="text-xs text-accent-green mt-1">{overview.active_companies} aktive</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-accent-green/20 rounded-lg flex items-center justify-center"><Users size={16} className="text-accent-green" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Brukere</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{overview.total_users}</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-accent-yellow/20 rounded-lg flex items-center justify-center"><Phone size={16} className="text-accent-yellow" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Denne uken</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{overview.calls_this_week}</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center"><TrendingUp size={16} className="text-purple-400" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Denne måneden</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{overview.calls_this_month}</div>
        </div>
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-accent-red/20 rounded-lg flex items-center justify-center"><DollarSign size={16} className="text-accent-red" /></div>
            <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Kostnad (mnd)</span>
          </div>
          <div className="text-2xl font-heading font-bold text-white">{formatCurrency(overview.total_cost_month || 0, 'NOK')}</div>
        </div>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-navy-600 flex items-center justify-between gap-4">
          <h3 className="font-heading text-base font-semibold text-white">Alle bedrifter <span className="text-sm font-normal text-slate-500">({filteredCompanies.length})</span></h3>
          <div className="relative">
            <input
              type="text"
              placeholder="Søk bedrift..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 bg-navy-700 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-accent-blue w-40"
            />
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500">🔍</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-companies-table">
            <thead>
              <tr className="border-b border-navy-600">
                <SortHeader col="name">Bedrift</SortHeader>
                <SortHeader col="subscription_tier">Plan</SortHeader>
                <SortHeader col="phone_number">Telefon</SortHeader>
                <SortHeader col="users_count">Brukere</SortHeader>
                <SortHeader col="calls_this_week" align="right">Uke</SortHeader>
                <SortHeader col="total_calls" align="right">Totalt</SortHeader>
                <SortHeader col="monthly_cost" align="right">Kostnad</SortHeader>
                <SortHeader col="monthly_revenue" align="right">Inntekt</SortHeader>
                <SortHeader col="margin_pct" align="right">Margin</SortHeader>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Siste samtale</th>
                <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c._id} onClick={() => navigate(`/admin/companies/${c._id}`)}
                  className="border-b border-navy-600/50 hover:bg-navy-700 cursor-pointer transition-colors" data-testid={`admin-company-row-${c._id}`}>
                  <td className="px-4 py-3.5 text-sm font-medium text-white">{c.name}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      c.subscription_tier === 'Bedrift' ? 'bg-purple-500/20 text-purple-400' :
                      c.subscription_tier === 'Pro' ? 'bg-accent-blue/20 text-accent-blue' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>{c.subscription_tier || 'Start'}</span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-slate-400 font-mono">{c.phone_number}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400">{c.users_count}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400 font-mono text-right">{c.calls_this_week}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-400 font-mono text-right">{c.total_calls}</td>
                  <td className="px-4 py-3.5 text-sm text-accent-red font-mono text-right">{formatCurrency(c.monthly_cost || 0, 'NOK')}</td>
                  <td className="px-4 py-3.5 text-sm text-white font-mono text-right">{formatCurrency(c.monthly_revenue || 0, 'NOK')}</td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={`text-sm font-mono font-medium ${(c.margin_pct||0) >= 70 ? 'text-accent-green' : (c.margin_pct||0) >= 40 ? 'text-accent-yellow' : 'text-accent-red'}`}>
                      {c.margin_pct || 0}%
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-slate-500">{timeAgo(c.last_call_at)}</td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${c.active ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'}`}>
                        {c.active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {c.payment_status === 'past_due' && (
                        <span className="text-xs text-accent-red" title="Betaling mislyktes">⚠️</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
